/* eslint-disable import/no-webpack-loader-syntax */
import React, { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Marker from "./Marker";
import Popup from "./MapPopup";
import { useTranslation } from "react-i18next";

function Map({ features, currentCenter }) {
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const [activeFeature, setActiveFeature] = useState();
  const [data, setData] = useState();
  const [isLoaded, setIsLoading] = useState(false);
  const { i18n } = useTranslation();

  const updateLayerData = useCallback(() => {
    if (!mapRef.current || !data?.length > 0) return;

    const map = mapRef.current;

    // Update turfs
    const turfFeatures =
      data
        ?.filter((feature) => feature?.turfs)
        ?.flatMap((feature) =>
          feature.turfs.map((turf) => ({
            type: "Feature",
            geometry: turf.geometry,
            properties: {
              id: turf.id,
            },
          }))
        ) || [];

    map.getSource("polygons")?.setData({
      type: "FeatureCollection",
      features: turfFeatures,
    });
  }, [data]);

  const handleMarkerClick = (feature, markerIndex) => {
    if (!mapRef.current) return;

    const markerCoordinates = feature.markers[markerIndex].coordinates;

    if (
      activeFeature?.id === feature.id &&
      activeFeature.markerIndex === markerIndex
    ) {
      return setActiveFeature(null);
    }

    setActiveFeature({ ...feature, markerIndex });
    mapRef.current.flyTo({
      center: markerCoordinates,
      easing: (t) => t,
    });
  };

  const handleClosePopup = () => {
    setActiveFeature(null);
  };

  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      center: [1.7191036, 46.71109],
      style: "mapbox://styles/mapbox/light-v10",
      zoom: 5,
      language: i18n.language?.includes("en") ? "en" : "fr",
    });

    mapRef.current = map;

    map.on("load", () => {
      setIsLoading(false);
      map.addSource("polygons", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: "polygons",
        type: "fill",
        source: "polygons",
        paint: {
          "fill-color": "#577b87",
          "fill-opacity": 0.25,
        },
      });
      setData(features.slice());
      setIsLoading(true);
    });

    return () => {
      map.remove();
    };
  }, [i18n.language, features]);

  useEffect(() => {
    updateLayerData();
  }, [updateLayerData]);

  useEffect(() => {
    if (mapRef.current) {
      setData(features.slice());
    }
  }, [features]);

  useEffect(() => {
    if (!currentCenter || !mapRef.current) return;
    mapRef.current.flyTo({
      center: currentCenter,
      zoom: 7,
      speed: 5,
      curve: 1,
      easing: (t) => t,
    });
  }, [currentCenter]);

  return (
    <>
      <div id="map-container" className="map mb-5" ref={mapContainerRef} />
      {mapRef.current &&
        isLoaded &&
        data &&
        data.map((feature) => (
          <Marker
            key={feature.id}
            map={mapRef.current}
            feature={feature}
            isActive={activeFeature?.id === feature.id}
            onClick={handleMarkerClick}
          />
        ))}
      {mapRef.current && (
        <Popup
          map={mapRef.current}
          activeFeature={activeFeature}
          onClose={handleClosePopup}
        />
      )}
    </>
  );
}

export default Map;
